import { useState, useEffect, useCallback } from 'react';
import { emailTemplatesAPI } from '../utils/api';
import { logger } from '../utils/logger';

// Interface alignee sur le modele Prisma EmailTemplate (schema.prisma:664)
export interface EmailTemplate {
  id: string;
  tenantId: string;
  companyId: string;
  code: string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  variables: Record<string, any> | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Templates par defaut integres (fallback quand la DB est vide)
 */
const BUILTIN_TEMPLATES: Record<string, EmailTemplate> = {
  invoiceSent: {
    id: 'builtin-invoice-sent',
    tenantId: '',
    companyId: '',
    code: 'invoiceSent',
    name: 'Envoi de facture',
    subject: 'Facture {{invoiceNumber}} - {{companyName}}',
    bodyHtml: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <p>Bonjour {{clientName}},</p>
  <p>Veuillez trouver ci-joint la facture <strong>{{invoiceNumber}}</strong> d'un montant de <strong>{{total}}</strong>, avec une date d'échéance au <strong>{{dueDate}}</strong>.</p>
  <p>Vous pouvez également consulter et télécharger votre facture en ligne :</p>
  <p><a href="{{publicLink}}" style="display: inline-block; padding: 10px 24px; background-color: #0d9488; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;">Voir la facture</a></p>
  <p>Conditions de règlement : {{paymentConditions}}</p>
  <p>Cordialement,<br/><strong>{{companyName}}</strong></p>
</div>`,
    bodyText: null,
    variables: null,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  quoteSent: {
    id: 'builtin-quote-sent',
    tenantId: '',
    companyId: '',
    code: 'quoteSent',
    name: 'Envoi de devis',
    subject: 'Devis {{quoteNumber}} - {{companyName}}',
    bodyHtml: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <p>Bonjour {{clientName}},</p>
  <p>Veuillez trouver ci-joint le devis <strong>{{quoteNumber}}</strong> d'un montant de <strong>{{total}}</strong>.</p>
  <p>Vous pouvez également consulter votre devis en ligne et l'accepter directement :</p>
  <p><a href="{{publicLink}}" style="display: inline-block; padding: 10px 24px; background-color: #0d9488; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;">Voir le devis</a></p>
  <p>N'hésitez pas à nous contacter pour toute question.</p>
  <p>Cordialement,<br/><strong>{{companyName}}</strong></p>
</div>`,
    bodyText: null,
    variables: null,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  creditNoteSent: {
    id: 'builtin-credit-note-sent',
    tenantId: '',
    companyId: '',
    code: 'creditNoteSent',
    name: "Envoi d'avoir",
    subject: 'Avoir {{creditNoteNumber}} - {{companyName}}',
    bodyHtml: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <p>Bonjour {{clientName}},</p>
  <p>Veuillez trouver ci-joint l'avoir <strong>{{creditNoteNumber}}</strong> d'un montant de <strong>{{total}}</strong>.</p>
  <p>Vous pouvez également consulter votre avoir en ligne :</p>
  <p><a href="{{publicLink}}" style="display: inline-block; padding: 10px 24px; background-color: #0d9488; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;">Voir l'avoir</a></p>
  <p>Cordialement,<br/><strong>{{companyName}}</strong></p>
</div>`,
    bodyText: null,
    variables: null,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
};

interface UseEmailTemplatesReturn {
  templates: EmailTemplate[];
  loading: boolean;
  error: string | null;
  getTemplatesByCode: (code: string) => EmailTemplate[];
  getTemplateByCode: (code: string) => EmailTemplate | null;
  refreshTemplates: () => Promise<void>;
  renderTemplate: (template: EmailTemplate, variables: Record<string, any>) => { subject: string; bodyHtml: string; bodyText: string };
}

export const useEmailTemplates = (code?: string): UseEmailTemplatesReturn => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);

    try {
      logger.log('[useEmailTemplates] Chargement des templates via API Layer:', { code });

      const data = await emailTemplatesAPI.getAll(code);
      const apiTemplates: EmailTemplate[] = data.data || [];
      logger.log('[useEmailTemplates] Templates charges:', apiTemplates.length);

      if (apiTemplates.length > 0) {
        setTemplates(apiTemplates);
      } else {
        // Fallback: utiliser les templates integres
        logger.log('[useEmailTemplates] Aucun template DB, utilisation des templates integres');
        if (code && BUILTIN_TEMPLATES[code]) {
          setTemplates([BUILTIN_TEMPLATES[code]]);
        } else if (!code) {
          setTemplates(Object.values(BUILTIN_TEMPLATES));
        } else {
          setTemplates([]);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(message);
      logger.error('[useEmailTemplates] Erreur:', message);

      // Fallback en cas d'erreur: utiliser les templates integres
      if (code && BUILTIN_TEMPLATES[code]) {
        setTemplates([BUILTIN_TEMPLATES[code]]);
      } else if (!code) {
        setTemplates(Object.values(BUILTIN_TEMPLATES));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [code]);

  const getTemplatesByCode = useCallback((templateCode: string) => {
    return templates.filter((t) => t.code === templateCode && t.isActive);
  }, [templates]);

  const getTemplateByCode = useCallback((templateCode: string) => {
    return templates.find((t) => t.code === templateCode && t.isActive) || null;
  }, [templates]);

  const refreshTemplates = useCallback(async () => {
    await fetchTemplates();
  }, []);

  /**
   * Remplace les variables dans un template
   * Variables format: {{variableName}} (camelCase, double accolades)
   */
  const renderTemplate = useCallback((
    template: EmailTemplate,
    variables: Record<string, any>
  ): { subject: string; bodyHtml: string; bodyText: string } => {
    let subject = template.subject;
    let bodyHtml = template.bodyHtml;
    let bodyText = template.bodyText || '';

    Object.keys(variables).forEach((key) => {
      const value = variables[key] || '';
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      subject = subject.replace(regex, String(value));
      bodyHtml = bodyHtml.replace(regex, String(value));
      if (bodyText) {
        bodyText = bodyText.replace(regex, String(value));
      }
    });

    return { subject, bodyHtml, bodyText };
  }, []);

  return {
    templates,
    loading,
    error,
    getTemplatesByCode,
    getTemplateByCode,
    refreshTemplates,
    renderTemplate,
  };
};
